import { booleanParam } from '@/lib/search-params'

import type {
  DataGridColumnSchema,
  DataGridCriteria,
  DataGridFilter,
  DataGridFilterValue,
  DataGridParameterSchema,
  DataGridParameterType,
  DataGridParameterValue,
  DataGridParameterValueByType,
  DataGridSort,
} from './types'

const VALID_DIRECTIONS = new Set(['asc', 'desc'])

type DataGridUrlColumns = ReadonlySet<string> | Readonly<Record<string, DataGridColumnSchema>>
type DataGridUrlParameters = Readonly<Record<string, DataGridParameterSchema>> | undefined

const PARAMETER_DECODERS: {
  [Type in DataGridParameterType]: (raw: unknown) => DataGridParameterValueByType[Type] | undefined
} = {
  boolean: booleanParam,
}

type DataGridCriteriaSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
}

/** The grid's URL keys. Declared parameters appear under their own ids. */
export type DataGridUrlSearch = DataGridCriteriaSearch & {
  columns?: string
  [parameter: string]: string | DataGridParameterValue | undefined
}

export function encodeDataGridUrlState(criteria: DataGridCriteria): DataGridCriteriaSearch {
  return {
    q: criteria.search?.query || undefined,
    qColumns: criteria.search?.columns?.length
      ? criteria.search.columns.join(',')
      : undefined,
    filters: criteria.filter?.length ? JSON.stringify(criteria.filter) : undefined,
    sort: criteria.sort?.length
      ? criteria.sort.map(({ column, direction }) => `${column}:${direction}`).join(',')
      : undefined,
  }
}

export function decodeDataGridUrlState<Column extends string>(
  search: Record<string, unknown>,
  columns: ReadonlySet<string> | Readonly<Record<string, DataGridColumnSchema>>,
): DataGridCriteria<Column> {
  const columnIds = isColumnSet(columns) ? columns : new Set(Object.keys(columns))
  const query = typeof search.q === 'string' && search.q.trim()
    ? search.q
    : undefined
  const searchColumns = typeof search.qColumns === 'string'
    ? search.qColumns.split(',').filter((column): column is Column =>
        columnIds.has(column) && (isColumnSet(columns) || columns[column]?.searchable === true))
    : []
  const sort = decodeSort<Column>(search.sort, columns)
  const filter = decodeDataGridFilters<Column>(search.filters, columns)
  return {
    search: query || searchColumns.length
      ? { query, columns: searchColumns.length ? searchColumns : undefined }
      : undefined,
    sort: sort.length ? sort : undefined,
    filter: filter.length ? filter : undefined,
  }
}

function decodeSort<Column extends string>(
  raw: unknown,
  columns: ReadonlySet<string> | Readonly<Record<string, DataGridColumnSchema>>,
): DataGridSort<Column>[] {
  if (typeof raw !== 'string') return []
  return raw.split(',').flatMap((entry) => {
    const [column, direction, extra] = entry.split(':')
    const validColumn = column && (isColumnSet(columns)
      ? columns.has(column)
      : columns[column]?.sortable === true)
    return column && direction && !extra && validColumn
      && VALID_DIRECTIONS.has(direction)
      ? [{ column: column as Column, direction: direction as 'asc' | 'desc' }]
      : []
  })
}

export function normalizeDataGridUrlState(
  search: Record<string, unknown>,
  contract: { columns: DataGridUrlColumns; parameters?: DataGridUrlParameters },
): DataGridUrlSearch {
  return {
    ...encodeDataGridUrlState(decodeDataGridUrlState(search, contract.columns)),
    columns: encodeDataGridColumns(decodeDataGridColumns(search.columns, contract.columns)),
    ...encodeDataGridParameters(contract.parameters, decodeDataGridParameters(search, contract.parameters)),
  }
}

export function encodeDataGridColumns(columns: readonly string[] | undefined): string | undefined {
  return columns?.length ? columns.join(',') : undefined
}

export function decodeDataGridColumns<Column extends string>(
  raw: unknown,
  columns: DataGridUrlColumns,
): Column[] | undefined {
  if (typeof raw !== 'string') return undefined
  const columnIds = isColumnSet(columns) ? columns : new Set(Object.keys(columns))
  const decoded = [...new Set(raw.split(','))]
    .filter((column): column is Column => columnIds.has(column))
  return decoded.length ? decoded : undefined
}

/** Writes every declared parameter, leaving defaults out so the URL drops them. */
export function encodeDataGridParameters(
  parameters: DataGridUrlParameters,
  values: Readonly<Record<string, DataGridParameterValue | undefined>>,
): Record<string, DataGridParameterValue | undefined> {
  return Object.fromEntries(Object.values(parameters ?? {}).map((parameter) => [
    parameter.id,
    JSON.stringify(values[parameter.id]) === JSON.stringify(parameter.default) ? undefined : values[parameter.id],
  ]))
}

/** Reads every declared parameter, using its default when the value is missing or not of the declared type. */
export function decodeDataGridParameters(
  values: Readonly<Record<string, unknown>>,
  parameters: DataGridUrlParameters,
): Record<string, DataGridParameterValue> {
  return Object.fromEntries(Object.values(parameters ?? {}).map((parameter) => [
    parameter.id,
    PARAMETER_DECODERS[parameter.type](values[parameter.id]) ?? parameter.default,
  ]))
}

export function serializeDataGridFilterQuery<Column extends string>(
  filters: readonly DataGridFilter<Column>[] | undefined,
): string {
  if (!filters?.length) return ''
  const canonical = canonicalizeDataGridFilters(filters)
  return new URLSearchParams({ filters: JSON.stringify(canonical) }).toString()
}

export function parseDataGridFilterQuery<Column extends string>(
  value: string,
  columns: ReadonlySet<string> | Readonly<Record<string, DataGridColumnSchema>>,
): DataGridFilter<Column>[] {
  const filters = decodeDataGridUrlState<Column>(
    Object.fromEntries(new URLSearchParams(value)),
    columns,
  ).filter
  if (!filters) return []
  return canonicalizeDataGridFilters(filters)
}

export function canonicalizeDataGridFilters<Column extends string>(
  filters: readonly DataGridFilter<Column>[],
): DataGridFilter<Column>[] {
  return [...filters].map((filter) => canonicalFilter(filter))
    .sort((left, right) => left.column.localeCompare(right.column)
      || left.operator.localeCompare(right.operator))
}

function canonicalFilter<Column extends string>(
  filter: DataGridFilter<Column>,
): DataGridFilter<Column> {
  return {
    column: filter.column,
    operator: filter.operator,
    values: canonicalFilterValue(filter.operator, filter.values),
  }
}

function canonicalFilterValue(
  operator: string,
  value: DataGridFilterValue,
): DataGridFilterValue {
  if (Array.isArray(value)) return [...new Set(value)].sort()
  if (operator === 'between' && objectWith(value, 'from', 'to')) {
    const range = value as Record<string, string>
    return { from: range.from!, to: range.to! }
  }
  if (operator === 'between' && numberRange(value)) {
    return { from: value.from, to: value.to }
  }
  if (operator === 'relative' && typeof value === 'object' && value !== null
    && 'amount' in value && 'unit' in value) {
    const relative = value as { amount: number; unit: 'hours' | 'days' }
    return { amount: relative.amount, unit: relative.unit }
  }
  return value
}

export function normalizeDataGridFilterQuery(
  value: string,
  columns: ReadonlySet<string> | Readonly<Record<string, DataGridColumnSchema>>,
): string {
  return serializeDataGridFilterQuery(parseDataGridFilterQuery(value, columns))
}

export function decodeDataGridFilters<Column extends string>(
  raw: unknown,
  columns?: ReadonlySet<string> | Readonly<Record<string, DataGridColumnSchema>>,
): DataGridFilter<Column>[] {
  try {
    const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!Array.isArray(parsed)) return []
    return parsed.filter((entry): entry is DataGridFilter<Column> => {
      if (typeof entry !== 'object' || entry === null
        || !('column' in entry) || typeof entry.column !== 'string'
        || !('operator' in entry) || typeof entry.operator !== 'string'
        || !('values' in entry)) return false
      const schema = columns && !isColumnSet(columns) ? columns[entry.column] : undefined
      if (columns && (isColumnSet(columns) ? !columns.has(entry.column) : !schema)) return false
      if (schema && !schema.operators.includes(entry.operator)) return false
      return validOperatorValue(entry.operator, entry.values, schema?.kind)
    })
  } catch {
    return []
  }
}

function isColumnSet(
  columns: ReadonlySet<string> | Readonly<Record<string, DataGridColumnSchema>>,
): columns is ReadonlySet<string> {
  return 'has' in columns && typeof columns.has === 'function'
}

function validOperatorValue(
  operator: string,
  value: unknown,
  kind?: DataGridColumnSchema['kind'],
): boolean {
  if (operator === 'empty') return typeof value === 'boolean'
  if (operator === 'in' || operator === 'notIn') {
    return Array.isArray(value) && value.every((entry) => typeof entry === 'string')
  }
  if (operator === 'between') {
    return kind === 'number' ? numberRange(value)
      : kind === undefined ? numberRange(value) || objectWith(value, 'from', 'to')
        : objectWith(value, 'from', 'to')
  }
  if (kind === 'number' || operator === 'atLeast' || operator === 'atMost') {
    return validNumber(value)
  }
  if (operator === 'relative') {
    return typeof value === 'object' && value !== null
      && 'amount' in value && typeof value.amount === 'number' && value.amount > 0
      && 'unit' in value && (value.unit === 'hours' || value.unit === 'days')
  }
  if (kind === undefined && operator === 'equals') {
    return typeof value === 'string' || validNumber(value)
  }
  return typeof value === 'string'
}

function validNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value)
    && value >= -2147483648 && value <= 2147483647
}

function numberRange(value: unknown): value is { from: number; to: number } {
  return typeof value === 'object' && value !== null
    && 'from' in value && validNumber(value.from)
    && 'to' in value && validNumber(value.to)
    && value.from <= value.to
}

function objectWith(value: unknown, first: string, second: string): boolean {
  return typeof value === 'object' && value !== null
    && first in value && typeof value[first as keyof typeof value] === 'string'
    && second in value && typeof value[second as keyof typeof value] === 'string'
}
